import { useEffect, useRef } from "react";
import { Bell, BellSlash, BellRinging } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  notificationSupported,
  requestNotificationPermission,
  showLocalNotification,
} from "@/lib/pwa";

/**
 * Bell button: prompts to enable notifications, then polls /client/notifications
 * (or /admin/notifications) every 60s and triggers OS notification for new ones.
 */
export const NotificationBell = ({ scope = "client" }) => {
  const seenIds = useRef(new Set(JSON.parse(localStorage.getItem("fidelis_seen_notif_ids") || "[]")));
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!notificationSupported()) return;
    if (Notification.permission !== "granted") return;
    startPolling();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    if (intervalRef.current) return;
    const tick = async () => {
      try {
        const url = scope === "admin" ? "/admin/notifications" : "/client/notifications";
        const { data } = await api.get(url);
        for (const n of data) {
          if (!seenIds.current.has(n.id)) {
            seenIds.current.add(n.id);
            // skip on first run to avoid flood
            if (intervalRef.current.firstRunDone) {
              showLocalNotification(n.title || "Zeferius Pay", n.message || "");
            }
          }
        }
        localStorage.setItem("fidelis_seen_notif_ids", JSON.stringify(Array.from(seenIds.current).slice(-200)));
        intervalRef.current.firstRunDone = true;
      } catch {}
    };
    intervalRef.current = setInterval(tick, 60_000);
    intervalRef.current.firstRunDone = false;
    tick();
  };

  const stopPolling = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const enable = async () => {
    if (!notificationSupported()) {
      toast.error("Seu dispositivo não suporta notificações.");
      return;
    }
    const r = await requestNotificationPermission();
    if (r === "granted") {
      toast.success("Notificações ativadas!");
      showLocalNotification("Zeferius Pay", "Você receberá lembretes automáticos por aqui.");
      startPolling();
    } else if (r === "denied") {
      toast.error("Permissão negada. Ative nas configurações do navegador.");
    }
  };

  if (!notificationSupported()) return null;
  const granted = Notification.permission === "granted";

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={granted ? undefined : enable}
      data-testid={granted ? "notif-enabled" : "notif-enable-btn"}
      title={granted ? "Notificações ativas" : "Ativar notificações"}
      className={granted ? "text-blue-600" : "text-zinc-600"}
    >
      {granted ? <BellRinging size={18} weight="fill"/> : <Bell size={18}/>}
    </Button>
  );
};

export default NotificationBell;
